import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner, Image, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import config from '../../config';
import Layout from '../layouts/Main';
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';
import { isValidGSTIN, isValidPAN } from '../../common/validators';
import PincodeSelector from '../PincodeSelector';
import PaymentTermsTab from './PaymentTermsTab';

// Popular SMTP providers with their settings
const smtpProviders = [
  { value: 'gmail', label: 'Gmail (smtp.gmail.com)', host: 'smtp.gmail.com', port: 587 },
  { value: 'outlook', label: 'Outlook/Hotmail (smtp-mail.outlook.com)', host: 'smtp-mail.outlook.com', port: 587 },
  { value: 'yahoo', label: 'Yahoo Mail (smtp.mail.yahoo.com)', host: 'smtp.mail.yahoo.com', port: 587 },
  { value: 'aol', label: 'AOL Mail (smtp.aol.com)', host: 'smtp.aol.com', port: 587 },
  { value: 'icloud', label: 'iCloud Mail (smtp.mail.me.com)', host: 'smtp.mail.me.com', port: 587 },
  { value: 'zoho', label: 'Zoho Mail (smtp.zoho.com)', host: 'smtp.zoho.com', port: 587 },
  { value: 'sendgrid', label: 'SendGrid (smtp.sendgrid.net)', host: 'smtp.sendgrid.net', port: 587 },
  { value: 'mailgun', label: 'Mailgun (smtp.mailgun.org)', host: 'smtp.mailgun.org', port: 587 },
  { value: 'amazonses', label: 'Amazon SES (email-smtp.region.amazonaws.com)', host: 'email-smtp.us-east-1.amazonaws.com', port: 587 },
  { value: 'godaddy', label: 'GoDaddy (smtpout.secureserver.net)', host: 'smtpout.secureserver.net', port: 465 },
  { value: 'hostgator', label: 'HostGator (mail.yourdomain.com)', host: 'mail.yourdomain.com', port: 587 },
  { value: 'bluehost', label: 'Bluehost (mail.yourdomain.com)', host: 'mail.yourdomain.com', port: 587 },
  { value: 'office365', label: 'Office 365 (smtp.office365.com)', host: 'smtp.office365.com', port: 587 }
];

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Permissions
  const canViewSettings = useHasPermission(PERMISSIONS.SETTINGS_VIEW);
  const canEditSettings = useHasPermission(PERMISSIONS.SETTINGS_EDIT);



  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  const [settings, setSettings] = useState({
    companyName: '',
    address: {
      street: '',
      town: '',
      city: '',
      state: '',
      pincode: ''
    },
    contactInfo: {
      phone: '',
      email: '',
      website: ''
    },
    businessInfo: {
      gstNumber: '',
      panNumber: '',
      cinNumber: ''
    },
    companyLogo: '',
    emailConfig: {
      smtpHost: '',
      smtpPort: 587,
      smtpUser: '',
      smtpPass: '',
      fromEmail: '',
      fromName: '',
      isEnabled: false
    },
    telegramConfig: {
      botToken: '',
      isEnabled: false
    },
    theme: {
      primaryColor: '#007BFF',
      secondaryColor: '#6C757D',
      accentColor: '#28A745',
      fontSize: '14px'
    },
    pdfSettings: {
      headerText: '',
      footerText: '',
      showLogo: true,
      logoPosition: 'left',
      showWatermark: false,
      watermarkText: ''
    },
    documentSettings: {
      purchaseOrder: {
        prefix: 'PO',
        termsAndConditions: ''
      },
      salesOrder: {
        prefix: 'SO',
        termsAndConditions: ''
      },
      dispatch: {
        prefix: 'DN',
        termsAndConditions: ''
      }
    },
    signatures: {
      authorizedSignatory: {
        signature: ''
      },
      designation: ''
    },
    currency: {
      code: 'INR',
      symbol: '₹'
    },
    paymentTerms: {
      options: ['Advance', '4 Days', '7 Days', '10 Days', '30 days', '60 days']
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status !== 404) {
        setMessage({ type: 'danger', text: 'Failed to load settings' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });

    // Clear validation errors when user starts typing
    if (validationErrors[path]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[path];
        return newErrors;
      });
    }
  };

  // Validation function
  const validateField = (path, value) => {
    const errors = {};

    if (path === 'businessInfo.gstNumber' && value && !isValidGSTIN(value)) {
      errors[path] = 'Invalid GST number format. Must be 15 characters (e.g., 22AAAAA0000A1Z5)';
    }

    if (path === 'businessInfo.panNumber' && value && !isValidPAN(value)) {
      errors[path] = 'Invalid PAN number format. Must be 10 characters (e.g., AAAAA9999A)';
    }

    return errors;
  };



  // Handle business info validation on blur
  const handleBusinessInfoBlur = (field, value) => {
    const errors = validateField(`businessInfo.${field}`, value);
    setValidationErrors(prev => ({ ...prev, ...errors }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'logo') {
        setLogoFile(file);
      } else if (type === 'signature') {
        setSignatureFile(file);
      }
    }
  };

  const uploadFile = async (file, endpoint) => {
    const formData = new FormData();
    // Use the correct field name based on endpoint
    const fieldName = endpoint === 'upload-logo' ? 'logo' : 'signature';
    formData.append(fieldName, file);

    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${config.API_BASE_URL}/company-settings/${endpoint}`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');

      // Upload logo if selected
      if (logoFile) {
        await uploadFile(logoFile, 'upload-logo');
      }

      // Upload signature if selected
      if (signatureFile) {
        await uploadFile(signatureFile, 'upload-signature');
      }

      // Save settings
      const response = await axios.post(
        `${config.API_BASE_URL}/company-settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSettings(response.data);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setLogoFile(null);
      setSignatureFile(null);

      // Clear file inputs
      document.getElementById('logoInput').value = '';
      document.getElementById('signatureInput').value = '';

    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Failed to save settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want you want to delete the company logo?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/company-settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(prev => ({ ...prev, companyLogo: '' }));
      setMessage({ type: 'success', text: 'Logo deleted successfully' });
    } catch (error) {
      console.error('Error deleting logo:', error);
      setMessage({ type: 'danger', text: 'Failed to delete logo' });
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'warning', text: 'Please enter a test email address' });
      return;
    }

    setSendingTestEmail(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${config.API_BASE_URL}/company-settings/test-email`,
        { to: testEmail },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage({
        type: 'success',
        text: `Test email sent successfully to ${testEmail}! Check your inbox.`
      });
      setTestEmail(''); // Clear the test email field

    } catch (error) {
      console.error('Error sending test email:', error);
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Failed to send test email. Please check your email configuration.'
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading settings...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4">
        <h2 className="mb-4">Company Settings</h2>

        {message.text && (
          <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}
        {canViewSettings && (
          <Tabs defaultActiveKey="info" className="mb-4">
            <Tab eventKey="info" title="Company Information">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        readOnly={!canEditSettings}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">Address</h6>
                <Row>
                  <Col md={9}>
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.address?.street}
                        onChange={(e) => handleInputChange('address.street', e.target.value)}
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pincode</Form.Label>
                      <PincodeSelector
                        form={{
                          ...settings.address,
                          pin_code: settings.address?.pincode // Map pincode to pin_code for the component
                        }}
                        setForm={(updaterFunction) => {
                          // Call the updater function with current address to see what it returns
                          const mockPrev = {
                            ...settings.address,
                            pin_code: settings.address?.pincode
                          };
                          const updatedAddress = updaterFunction(mockPrev);
                          setSettings(prev => ({
                            ...prev,
                            address: {
                              ...prev.address,
                              ...updatedAddress,
                              pincode: updatedAddress.pin_code || prev.address.pincode // Map back to pincode
                            }
                          }));
                        }}
                        stepSubmitted={false}
                        name="pin_code"
                        populate={true}
                      />
                    </Form.Group>
                  </Col>
                  {/* <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Town</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.address?.town}
                        onChange={(e) => handleInputChange('address.town', e.target.value)}
                        readOnly={!canEditSettings}
                        placeholder="Auto-filled from pincode"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row> */}
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.address?.city}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                        readOnly={!canEditSettings}
                        placeholder="Auto-filled from pincode"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.address?.state}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                        readOnly={!canEditSettings}
                        placeholder="Auto-filled from pincode"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">Contact Information</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.contactInfo?.phone}
                        onChange={(e) => handleInputChange('contactInfo.phone', e.target.value)}
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={settings.contactInfo?.email}
                        onChange={(e) => handleInputChange('contactInfo.email', e.target.value)}
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Website</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.contactInfo?.website}
                        onChange={(e) => handleInputChange('contactInfo.website', e.target.value)}
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">Business Information</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>GST Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.businessInfo?.gstNumber || ''}
                        onChange={(e) => handleInputChange('businessInfo.gstNumber', e.target.value.toUpperCase())}
                        onBlur={(e) => handleBusinessInfoBlur('gstNumber', e.target.value)}
                        readOnly={!canEditSettings}
                        isInvalid={!!validationErrors['businessInfo.gstNumber']}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors['businessInfo.gstNumber']}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>PAN Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.businessInfo?.panNumber || ''}
                        onChange={(e) => handleInputChange('businessInfo.panNumber', e.target.value.toUpperCase())}
                        onBlur={(e) => handleBusinessInfoBlur('panNumber', e.target.value)}
                        readOnly={!canEditSettings}
                        isInvalid={!!validationErrors['businessInfo.panNumber']}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors['businessInfo.panNumber']}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>CIN Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.businessInfo?.cinNumber}
                        onChange={(e) => handleInputChange('businessInfo.cinNumber', e.target.value)}
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {canEditSettings && (
                  <div className="text-end mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving || !settings.companyName || Object.keys(validationErrors).length > 0}
                    >
                      {saving ? 'Saving...' : 'Save Information'}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Tab>

            <Tab eventKey="branding" title="Branding">
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Logo</Form.Label>
                      {settings.companyLogo && (
                        <div className="mb-2">
                          <Image src={`${config.DOC_BASE_URL}${settings.companyLogo}`} thumbnail style={{ maxHeight: '100px' }} />
                          {canEditSettings && (
                            <Button
                              variant="danger"
                              size="sm"
                              className="ms-2"
                              onClick={handleDeleteLogo}
                            >
                              Delete Logo
                            </Button>
                          )}
                        </div>
                      )}
                      {canEditSettings && (
                        <>
                          <Form.Control
                            id="logoInput"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'logo')}
                          />
                          <Form.Text className="text-muted">
                            Upload a new logo (Max 5MB, PNG/JPG/JPEG)
                          </Form.Text>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Authorized Signatory Image</Form.Label>
                      {settings.signatures?.authorizedSignatory?.signature && (
                        <div className="mb-2">
                          <Image src={`${config.DOC_BASE_URL}${settings.signatures?.authorizedSignatory.signature}`} thumbnail style={{ maxHeight: '100px' }} />
                        </div>
                      )}
                      {canEditSettings && (
                        <>
                          <Form.Control
                            id="signatureInput"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, 'signature')}
                          />
                          <Form.Text className="text-muted">
                            Upload signature image (Max 5MB, PNG/JPG/JPEG)
                          </Form.Text>
                        </>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Signatory Designation</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.signatures?.designation}
                        onChange={(e) => handleInputChange('signatures.designation', e.target.value)}
                        placeholder="e.g., Director, Manager"
                        readOnly={!canEditSettings}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {canEditSettings && (
                  <div className="text-end mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving || !settings.companyName}
                    >
                      {saving ? 'Saving...' : 'Save Branding'}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Tab>

            <Tab eventKey="email" title="Email Configuration">
              <Card.Body>
                {/* <Alert variant="info">
                  <strong>Note:</strong> Configure your email settings to send emails from your own SMTP server.
                  If not configured, the system will use default email settings.
                </Alert> */}

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Enable Custom Email Configuration"
                        checked={settings?.emailConfig?.isEnabled}
                        onChange={(e) => handleInputChange('emailConfig.isEnabled', e.target.checked)}
                        readOnly={!canEditSettings}
                      />
                      <Form.Text className="text-muted">
                        Check this to use your own SMTP settings instead of default configuration
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {settings?.emailConfig?.isEnabled && (
                  <>
                    <h6 className="mt-4 mb-3">SMTP Configuration</h6>

                    {/* Popular SMTP Providers Selector */}
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Popular SMTP Providers</Form.Label>
                          <Form.Select
                            value=""
                            onChange={(e) => {
                              const provider = smtpProviders.find(p => p.value === e.target.value);
                              if (provider) {
                                handleInputChange('emailConfig.smtpHost', provider.host);
                                handleInputChange('emailConfig.smtpPort', provider.port);
                              }
                            }}
                            readOnly={!canEditSettings}
                          >
                            <option value="">Select a provider (optional)</option>
                            {smtpProviders.map(provider => (
                              <option key={provider.value} value={provider.value}>
                                {provider.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Select a popular provider to auto-fill SMTP settings, or enter custom settings below
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>SMTP Host *</Form.Label>
                          <Form.Control
                            type="text"
                            value={settings.emailConfig?.smtpHost}
                            onChange={(e) => handleInputChange('emailConfig.smtpHost', e.target.value)}
                            placeholder="e.g., smtp.gmail.com"
                            readOnly={!canEditSettings}
                            required={settings.emailConfig?.isEnabled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>SMTP Port *</Form.Label>
                          <Form.Control
                            type="number"
                            value={settings.emailConfig?.smtpPort}
                            onChange={(e) => handleInputChange('emailConfig.smtpPort', parseInt(e.target.value))}
                            placeholder="587"
                            readOnly={!canEditSettings}
                            required={settings.emailConfig?.isEnabled}
                          />
                          <Form.Text className="text-muted">
                            Common ports: 587 (TLS), 465 (SSL), 25 (Plain)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>SMTP Username *</Form.Label>
                          <Form.Control
                            type="text"
                            value={settings.emailConfig?.smtpUser}
                            onChange={(e) => handleInputChange('emailConfig.smtpUser', e.target.value)}
                            placeholder="your-email@gmail.com"
                            readOnly={!canEditSettings}
                            required={settings.emailConfig?.isEnabled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>SMTP Password *</Form.Label>
                          <Form.Control
                            type="password"
                            value={settings.emailConfig?.smtpPass}
                            onChange={(e) => handleInputChange('emailConfig.smtpPass', e.target.value)}
                            placeholder="Your SMTP password or app password"
                            readOnly={!canEditSettings}
                            required={settings.emailConfig?.isEnabled}
                          />
                          <Form.Text className="text-muted">
                            For Gmail, use an App Password instead of your regular password
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h6 className="mt-4 mb-3">Sender Information</h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>From Email Address *</Form.Label>
                          <Form.Control
                            type="email"
                            value={settings.emailConfig?.fromEmail}
                            onChange={(e) => handleInputChange('emailConfig.fromEmail', e.target.value)}
                            placeholder="noreply@yourcompany.com"
                            readOnly={!canEditSettings}
                            required={settings.emailConfig?.isEnabled}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>From Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={settings.emailConfig?.fromName}
                            onChange={(e) => handleInputChange('emailConfig.fromName', e.target.value)}
                            placeholder="Your Company Name"
                            readOnly={!canEditSettings}
                          />
                          <Form.Text className="text-muted">
                            Optional: Display name for sent emails
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* <Alert variant="warning">
                      <strong>Security Notice:</strong> Email credentials are stored securely in your tenant database.
                      Make sure to use secure SMTP settings (TLS/SSL) and consider using app passwords for services like Gmail.
                    </Alert> */}
                  </>
                )}

                {/* Test Email Section */}
                <Alert variant="light">

                  <h6 className="mb-3">Test Email Configuration</h6>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Test Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter email address to send test"
                          onChange={(e) => setTestEmail(e.target.value)}
                          value={testEmail}
                        />
                        <Form.Text className="text-muted">
                          Enter an email address to send a test email and verify your configuration
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>&nbsp;</Form.Label>
                        <div>
                          <Button
                            variant="secondary"
                            onClick={handleSendTestEmail}
                            disabled={sendingTestEmail || !testEmail}
                            className="w-100"
                          >
                            {sendingTestEmail ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Sending...
                              </>
                            ) : (
                              'Send Test Email'
                            )}
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Alert>

                {canEditSettings && (
                  <div className="text-end mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving || !settings.companyName}
                    >
                      {saving ? 'Saving...' : 'Save Email Configuration'}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Tab>

            <Tab eventKey="telegram" title="Telegram Configuration">
              <Card.Body>
                <Alert variant="info">
                  <strong>Telegram Integration:</strong> Configure your Telegram Bot token to enable automated notifications.
                  You can get a token by messaging <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer">@BotFather</a> on Telegram.
                </Alert>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Enable Telegram Notifications"
                        checked={settings?.telegramConfig?.isEnabled}
                        onChange={(e) => handleInputChange('telegramConfig.isEnabled', e.target.checked)}
                        readOnly={!canEditSettings}
                      />
                      <Form.Text className="text-muted">
                        Check this to enable Telegram bot notifications for this company
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bot API Token *</Form.Label>
                      <Form.Control
                        type="text"
                        value={settings.telegramConfig?.botToken}
                        onChange={(e) => handleInputChange('telegramConfig.botToken', e.target.value)}
                        placeholder="e.g., 123456789:ABCDefGhIJKlmNoPQrsTUVwxyZ"
                        readOnly={!canEditSettings}
                        required={settings.telegramConfig?.isEnabled}
                      />
                      <Form.Text className="text-muted">
                        Enter the API token provided by @BotFather
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {canEditSettings && (
                  <div className="text-end mt-4">
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving || !settings.companyName}
                    >
                      {saving ? 'Saving...' : 'Save Telegram Configuration'}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Tab>

            <Tab eventKey="payment-terms" title="Payment Terms">
              <PaymentTermsTab />
            </Tab>

            {/* <Tab eventKey="theme" title="Theme Colors">
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Color</Form.Label>
                  <Form.Control
                    type="color"
                    value={settings.theme.primaryColor}
                    onChange={(e) => handleInputChange('theme.primaryColor', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Secondary Color</Form.Label>
                  <Form.Control
                    type="color"
                    value={settings.theme.secondaryColor}
                    onChange={(e) => handleInputChange('theme.secondaryColor', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Accent Color</Form.Label>
                  <Form.Control
                    type="color"
                    value={settings.theme.accentColor}
                    onChange={(e) => handleInputChange('theme.accentColor', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Font Size</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.theme.fontSize}
                    onChange={(e) => handleInputChange('theme.fontSize', e.target.value)}
                    readOnly={!canEditSettings}
                    placeholder="e.g., 14px"
                  />
                </Form.Group>
              </Col>
            </Row>

            {canEditSettings && (
            <div className="text-end mt-4">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !settings.companyName}
              >
                {saving ? 'Saving...' : 'Save Theme'}
              </Button>
            </div>
            )}
          </Card.Body>
        </Tab>

        <Tab eventKey="pdf" title="PDF Settings">
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Header Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.pdfSettings.headerText}
                    onChange={(e) => handleInputChange('pdfSettings.headerText', e.target.value)}
                    readOnly={!canEditSettings}
                    placeholder="Optional header text for PDFs"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Footer Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.pdfSettings.footerText}
                    onChange={(e) => handleInputChange('pdfSettings.footerText', e.target.value)}
                    readOnly={!canEditSettings}
                    placeholder="Optional footer text for PDFs"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Logo in PDF"
                    checked={settings.pdfSettings.showLogo}
                    onChange={(e) => handleInputChange('pdfSettings.showLogo', e.target.checked)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Logo Position</Form.Label>
                  <Form.Select
                    value={settings.pdfSettings.logoPosition}
                    onChange={(e) => handleInputChange('pdfSettings.logoPosition', e.target.value)}
                    readOnly={!canEditSettings}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Watermark"
                    checked={settings.pdfSettings.showWatermark}
                    onChange={(e) => handleInputChange('pdfSettings.showWatermark', e.target.checked)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Watermark Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.pdfSettings.watermarkText}
                    onChange={(e) => handleInputChange('pdfSettings.watermarkText', e.target.value)}
                    readOnly={!canEditSettings}
                    placeholder="e.g., CONFIDENTIAL"
                    disabled={!settings.pdfSettings.showWatermark}
                  />
                </Form.Group>
              </Col>
            </Row>

            {canEditSettings && (
            <div className="text-end mt-4">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !settings.companyName}
              >
                {saving ? 'Saving...' : 'Save PDF Settings'}
              </Button>
            </div>
            )}
          </Card.Body>
        </Tab>

        <Tab eventKey="documents" title="Document Settings">
          <Card.Body>
            <h6>Purchase Order</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Number Prefix</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.documentSettings.purchaseOrder.prefix}
                    onChange={(e) => handleInputChange('documentSettings.purchaseOrder.prefix', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group className="mb-3">
                  <Form.Label>Terms & Conditions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={settings.documentSettings.purchaseOrder.termsAndConditions}
                    onChange={(e) => handleInputChange('documentSettings.purchaseOrder.termsAndConditions', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-4">Sales Order</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Number Prefix</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.documentSettings.salesOrder.prefix}
                    onChange={(e) => handleInputChange('documentSettings.salesOrder.prefix', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group className="mb-3">
                  <Form.Label>Terms & Conditions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={settings.documentSettings.salesOrder.termsAndConditions}
                    onChange={(e) => handleInputChange('documentSettings.salesOrder.termsAndConditions', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-4">Dispatch Note</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Number Prefix</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.documentSettings.dispatch.prefix}
                    onChange={(e) => handleInputChange('documentSettings.dispatch.prefix', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group className="mb-3">
                  <Form.Label>Terms & Conditions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={settings.documentSettings.dispatch.termsAndConditions}
                    onChange={(e) => handleInputChange('documentSettings.dispatch.termsAndConditions', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
            </Row>

            {canEditSettings && (
            <div className="text-end mt-4">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !settings.companyName}
              >
                {saving ? 'Saving...' : 'Save Document Settings'}
              </Button>
            </div>
            )}
          </Card.Body>
        </Tab>

        <Tab eventKey="currency" title="Currency">
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.currency.code}
                    onChange={(e) => handleInputChange('currency.code', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency Symbol</Form.Label>
                  <Form.Control
                    type="text"
                    value={settings.currency.symbol}
                    onChange={(e) => handleInputChange('currency.symbol', e.target.value)}
                    readOnly={!canEditSettings}
                  />
                </Form.Group>
              </Col>
            </Row>

            {canEditSettings && (
            <div className="text-end mt-4">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !settings.companyName}
              >
                {saving ? 'Saving...' : 'Save Currency'}
              </Button>
            </div>
            )}
          </Card.Body>
        </Tab> */}
          </Tabs>
        )}
      </div>
    </Layout>
  );
};

export default CompanySettings;
